import {FC, useEffect, useState} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {Box} from '@mui/material';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {ReactComponent as SuccessIcon} from '@icons/successfully.svg';
import {ReactComponent as SilentIcon} from '@icons/sidebar/silent-bid.svg';
import {ReactComponent as DashboardIcon} from '@icons/sidebar/dashboard.svg';
import {ReactComponent as TicketIcon} from '@icons/sidebar/ticket.svg';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
import {ReactComponent as LiveIcon} from '@icons/sidebar/live-bid.svg';
import {ReactComponent as TiketStarIcon} from '@icons/sidebar/ticket-star.svg';
import {ReactComponent as MoneyIcon} from '@icons/sidebar/money.svg';
import {ReactComponent as QuestionIcon} from '@icons/sidebar/question.svg';
import MakeDonationModal, {donationOptions} from '@/components/User/MakeDonationModal';
import {EVENT_ID, OUTLINE} from '@/utils/constants';
import {PAYMENT_METHOD, ROUTER_URL} from '@/utils/enums';
import Button from '@components/Button';
import Modal from '@/shared/components/Modal';
import NeedBeLoggedModal from '@/components/User/NeedBeLogged';
import AddTableTicket from '@/components/Organization/TablesAndTickets/AddTableTicket';
import {OrganizationAttendeesService} from '@/utils/services';
import {getUserInfo, getUserSelector} from '@/store/user';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {useSelector} from 'react-redux';
import {getDonationListByEvent, userMakeDonation} from '@/store/user-donation/actions';
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
import {userSidebarStyle} from './UserSidebar.styles';
import {toast} from 'react-toastify';
import {getCurrentEventSelector} from '@/store/organization-event';
import {clearPrivateTicketsTiers, getTicketsList, getTicketsTiers} from '@/store/organization-tables';
import {userAPI} from '@/core/api';
import {usePaymentStatusPolling} from '@/utils/hooks';
import PaymentOptions from '@/pages/User/Profile/General/PaymentOptions';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import {profileContactList} from '@/pages/User/Profile/General/ProfileGeneral.style';
import {useCardAttachment, WaitResult} from '@/utils/hooks/useCardAttachment';
import axios from 'axios';

const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const UserSidebar: FC = () => {
  const dispatch = useAppDispatch();
  const user = useSelector(getUserSelector);
  const event = useAppSelector(getCurrentEventSelector);
  const location = useLocation();

  const {isStarting, isWaiting, waitForAttachment, startAttachment} = useCardAttachment(event?.id, location.pathname);

  const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
  const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isSuccessModalOpen2, setIsSuccessModalOpen2] = useState(false);
  const [selectedOption, setSelectedOption] = useState(donationOptions[0]);
  const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [error, setError] = useState('');
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  useEffect(() => {
    if (user?.paymentMethod) setPaymentOption(user.paymentMethod);
  }, [user?.paymentMethod]);

  // NEW: тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  const {startPollingFromUrl} = usePaymentStatusPolling({
    apiInstance: userAPI,
  });

  const onSuccess = () => {
    setIsSuccessModalOpen2(true);
  };

  const handleNextStep = () => setPaymentStep(2);
  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  // ---------- Payment: старе (не-карткові способи) ----------
  const addPaymentOption = async () => {
    if (!user?.bidderId || !user?.id) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }

      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ---------- Payment: Stripe-флоу для картки (NEW) ----------
  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!', {toastId: 'card-attached'});
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  const handleAttentionModalChange = (title: string, description: string) => {
    if (title && description) {
      setModalTitle(title);
      setModalDescription(description);
    }
    setIsAttentionModalOpen(!isAttentionModalOpen);
  };

  const handleAddTicketModalShow = async () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else if (!event?.tableInfos.length) {
      handleAttentionModalChange(
        'Table Tiers Coming Soon',
        'The organizer hasn`t set up the table tiers yet. Please check back later'
      );
    } else {
      await getTableData();
      setIsShowAddTicketModal(true);
    }
  };

  const handleMakeDonationModalShow = () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else if (!event?.isFundTheMission) {
      handleAttentionModalChange(
        'Thank You for Wanting to Help!',
        'The organizer has paused donation for this auction.'
      );
    } else if (!user.paymentMethod) {
      setIsShowPaymentModal(true);
    } else {
      setIsDonationModalOpen(true);
      setSelectedOption(donationOptions[0]);
    }
  };

  const handleAddDonation = async () => {
    if (!user || !event) return;

    const numericAmount = parseFloat(selectedOption.replace(',', '.'));

    if (!selectedOption || Number.isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Donation amount should be greater than 0');
      return;
    }

    const reqBody = {
      amount: Number(numericAmount),
      eventId: event.id,
    };

    try {
      await dispatch(userMakeDonation({reqBody, apiInstance: userAPI})).unwrap();
      await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();
      setIsSuccessModalOpen(true);
      setIsDonationModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setError('');
    }
  };

  const getTableData = async () => {
    if (!event?.id) return;

    try {
      const reqBody = getDefaultRequestBody(event!.id, EVENT_ID);

      await Promise.all([
        dispatch(getTicketsList({reqBody, apiInstance: userAPI})).unwrap(),
        dispatch(getTicketsTiers({reqBody: event.id, apiInstance: userAPI})).unwrap(),
      ]);
      if (!location.pathname.endsWith(ROUTER_URL.USER_PURCHASE_TICKETS)) {
        dispatch(clearPrivateTicketsTiers());
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (selectedOption || Number(selectedOption) !== 0) setError('');
  }, [selectedOption]);

  useEffect(() => {
    startPollingFromUrl();
  }, []);

  return (
    <Box sx={userSidebarStyle.wrap}>
      <Box sx={userSidebarStyle.spacer} />
      <Box sx={userSidebarStyle.sidebar}>
        <Button
          startIcon={
            <Box sx={userSidebarStyle.iconWrapper}>
              <TicketIcon />
            </Box>
          }
          title="Buy Tickets"
          sx={userSidebarStyle.buttonCreate}
          onClick={handleAddTicketModalShow}
          disableRipple
        />
        <Button
          startIcon={
            <Box sx={userSidebarStyle.moneyIconWrapper}>
              <MoneyIcon />
            </Box>
          }
          variant={OUTLINE}
          title="Make Donation"
          onClick={handleMakeDonationModalShow}
          disableRipple
        />

        <Box sx={userSidebarStyle.menu}>
          <NavLink to="" end style={userSidebarStyle.menuItem}>
            <DashboardIcon />
            Event Info
          </NavLink>

          <NavLink to={ROUTER_URL.USER_TABLES} end style={userSidebarStyle.menuItem}>
            <TicketIcon />
            My Tables and Tickets
          </NavLink>

          <NavLink to={ROUTER_URL.USER_ALL_ITEMS} style={userSidebarStyle.menuItem}>
            <JudgeIcon />
            All Items
          </NavLink>

          <NavLink to={ROUTER_URL.USER_SILENT_AUCTION} style={userSidebarStyle.menuItem}>
            <SilentIcon /> Silent Auction
          </NavLink>
          <NavLink to={ROUTER_URL.USER_LIVE_AUCTION} style={userSidebarStyle.menuItem}>
            <LiveIcon /> Live Auction
          </NavLink>

          {event?.isRaffle && (
            <NavLink to={ROUTER_URL.USER_RAFFLE} style={userSidebarStyle.menuItem}>
              <TiketStarIcon /> Raffle
            </NavLink>
          )}

          {event?.isFundTheMission && (
            <NavLink to={ROUTER_URL.USER_FUND_MISSIONS} style={userSidebarStyle.menuItem}>
              <MoneyIcon /> Fund the mission
            </NavLink>
          )}

          <NavLink to={ROUTER_URL.USER_HELP_AND_SUPPORT} style={userSidebarStyle.menuItem}>
            <QuestionIcon /> Help & Support
          </NavLink>
        </Box>
      </Box>

      <Modal
        onClose={() => setIsShowAddTicketModal(false)}
        open={isShowAddTicketModal}
        sx={userSidebarStyle.modalTickets}
        title="Buy Tickets"
      >
        <AddTableTicket onCloseModal={() => setIsShowAddTicketModal(false)} onSuccess={onSuccess} />
      </Modal>
      <Modal
        onClose={() => setIsNeedBeLoggedModalOpen(false)}
        open={isNeedBeLoggedModalOpen}
        sx={userSidebarStyle.modalLogin}
        mainIcon={<DangerIcon />}
      >
        <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
      </Modal>
      <Modal
        isShowSaveButton
        mainButtonTitle={`Donate $${selectedOption}`}
        isSaveButtonDisable={!selectedOption}
        isShowCloseButton
        closeButtonTitle="Close"
        onClose={() => {
          setIsDonationModalOpen(false);
          setError('');
        }}
        open={isDonationModalOpen}
        sx={userSidebarStyle.modalBid}
        onSave={handleAddDonation}
      >
        <MakeDonationModal selectedOption={selectedOption} setSelectedOption={setSelectedOption} error={error} />
      </Modal>
      <Modal
        open={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setSelectedOption(donationOptions[0]);
        }}
        title="Thank You for Your Donation"
        description={`Your donation of $${selectedOption} successfully processed.`}
        sx={userSidebarStyle.modalLogin}
        closeButtonTitle="Done"
        mainIcon={<SuccessIcon />}
      />
      <Modal
        open={isSuccessModalOpen2}
        onClose={() => setIsSuccessModalOpen2(false)}
        title="Thank You For Your Support!"
        description="Your tickets have been successfully purchased. A receipt and additional information will be delivered by email."
        sx={userSidebarStyle.modalLogin}
        mainButtonTitle="Done"
        onSave={() => setIsSuccessModalOpen2(false)}
        isShowSaveButton
        mainIcon={<SuccessIcon />}
      />
      <Modal
        open={isAttentionModalOpen}
        onClose={() => handleAttentionModalChange('', '')}
        title={modalTitle}
        description={modalDescription}
        sx={userSidebarStyle.modalLogin}
      />
      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          isSaveButtonDisable={isStarting}
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default UserSidebar;

// const UserSidebar: FC = () => {
//   const dispatch = useAppDispatch();
//   const user = useSelector(getUserSelector);
//   const event = useAppSelector(getCurrentEventSelector);
//   const location = useLocation();
//   // console.log(location);

//   const [isShowAddTicketModal, setIsShowAddTicketModal] = useState<boolean>(false);
//   const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);
//   const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
//   const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
//   const [isSuccessModalOpen2, setIsSuccessModalOpen2] = useState(false);
//   const [selectedOption, setSelectedOption] = useState(donationOptions[0]);
//   const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);
//   const [modalTitle, setModalTitle] = useState('');
//   const [modalDescription, setModalDescription] = useState('');
//   const [error, setError] = useState('');
//   const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
//   const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
//   const [paymentOption, setPaymentOption] = useState(
//     user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
//   );

//   useEffect(() => {
//     if (user?.paymentMethod) setPaymentOption(user.paymentMethod);
//   }, [user?.paymentMethod]);

//   const {startPollingFromUrl} = usePaymentStatusPolling({
//     // onCompleted: () => {
//     //   getDonationsListData();
//     //   setIsSuccessModalOpen(true);
//     // },
//     apiInstance: userAPI,
//   });

//   const onSuccess = () => {
//     setIsSuccessModalOpen2(true);
//   };

//   const addPaymentOption = async () => {
//     if (!user?.bidderId || !user?.id) return;
//     try {
//       const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
//       if (res.status === 204) {
//         await dispatch(getUserInfo(user?.id)).unwrap();
//         toast.success('Payment method added successfully!');
//       }

//       handleClosePaymentModal();
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     }
//   };
//   const handleNextStep = () => setPaymentStep(2);
//   const handleClosePaymentModal = () => {
//     setIsShowPaymentModal(false);
//     setPaymentStep(1);
//   };

//   const handleAttentionModalChange = (title: string, description: string) => {
//     if (title && description) {
//       setModalTitle(title);
//       setModalDescription(description);
//     }
//     setIsAttentionModalOpen(!isAttentionModalOpen);
//   };

//   const handleAddTicketModalShow = async () => {
//     if (!user) {
//       setIsNeedBeLoggedModalOpen(true);
//     } else if (!event?.tableInfos.length) {
//       handleAttentionModalChange(
//         'Table Tiers Coming Soon',
//         'The organizer hasn`t set up the table tiers yet. Please check back later'
//       );
//     } else {
//       await getTableData();
//       setIsShowAddTicketModal(true);
//     }
//   };

//   const handleMakeDonationModalShow = () => {
//     if (!user) {
//       setIsNeedBeLoggedModalOpen(true);
//     } else if (!event?.isFundTheMission) {
//       handleAttentionModalChange(
//         'Thank You for Wanting to Help!',
//         'The organizer has paused donation for this auction.'
//       );
//     } else if (!user.paymentMethod) {
//       setIsShowPaymentModal(true);
//     } else {
//       setIsDonationModalOpen(true);
//       setSelectedOption(donationOptions[0]);
//     }
//   };

//   const handleAddDonation = async () => {
//     if (!user || !event) return;

//     const numericAmount = parseFloat(selectedOption.replace(',', '.'));

//     if (!selectedOption || Number.isNaN(numericAmount) || numericAmount <= 0) {
//       toast.error('Donation amount should be greater than 0');
//       return;
//     }

//     const reqBody = {
//       amount: Number(numericAmount),
//       eventId: event.id,
//     };

//     try {
//       await dispatch(userMakeDonation({reqBody, apiInstance: userAPI})).unwrap();
//       await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();
//       setIsSuccessModalOpen(true);
//       setIsDonationModalOpen(false);
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     } finally {
//       setError('');
//     }
//   };

//   const getTableData = async () => {
//     if (!event?.id) return;

//     try {
//       const reqBody = getDefaultRequestBody(event!.id, EVENT_ID);

//       await Promise.all([
//         dispatch(getTicketsList({reqBody, apiInstance: userAPI})).unwrap(),
//         dispatch(getTicketsTiers({reqBody: event.id, apiInstance: userAPI})).unwrap(),
//       ]);
//       if (!location.pathname.endsWith(ROUTER_URL.USER_PURCHASE_TICKETS)) {
//         dispatch(clearPrivateTicketsTiers());
//       }
//     } catch (error) {
//       toast.error(getErrorMessage(error));
//     }
//   };

//   useEffect(() => {
//     if (selectedOption || Number(selectedOption) !== 0) setError('');
//   }, [selectedOption]);

//   useEffect(() => {
//     startPollingFromUrl();
//   }, []);

//   return (
//     <Box sx={userSidebarStyle.wrap}>
//       <Box sx={userSidebarStyle.spacer} />
//       <Box sx={userSidebarStyle.sidebar}>
//         <Button
//           startIcon={
//             <Box sx={userSidebarStyle.iconWrapper}>
//               <TicketIcon />
//             </Box>
//           }
//           title="Buy Tickets"
//           sx={userSidebarStyle.buttonCreate}
//           onClick={handleAddTicketModalShow}
//           disableRipple
//         />
//         <Button
//           startIcon={
//             <Box sx={userSidebarStyle.moneyIconWrapper}>
//               <MoneyIcon />
//             </Box>
//           }
//           variant={OUTLINE}
//           title="Make Donation"
//           onClick={handleMakeDonationModalShow}
//           disableRipple
//         />

//         <Box sx={userSidebarStyle.menu}>
//           <NavLink to="" end style={userSidebarStyle.menuItem}>
//             <DashboardIcon />
//             Event Info
//           </NavLink>

//           <NavLink to={ROUTER_URL.USER_TABLES} end style={userSidebarStyle.menuItem}>
//             <TicketIcon />
//             My Tables and Tickets
//           </NavLink>

//           <NavLink to={ROUTER_URL.USER_ALL_ITEMS} style={userSidebarStyle.menuItem}>
//             <JudgeIcon />
//             All Items
//           </NavLink>

//           <NavLink to={ROUTER_URL.USER_SILENT_AUCTION} style={userSidebarStyle.menuItem}>
//             <SilentIcon /> Silent Auction
//           </NavLink>
//           <NavLink to={ROUTER_URL.USER_LIVE_AUCTION} style={userSidebarStyle.menuItem}>
//             <LiveIcon /> Live Auction
//           </NavLink>

//           {event?.isRaffle && (
//             <NavLink to={ROUTER_URL.USER_RAFFLE} style={userSidebarStyle.menuItem}>
//               <TiketStarIcon /> Raffle
//             </NavLink>
//           )}

//           {event?.isFundTheMission && (
//             <NavLink to={ROUTER_URL.USER_FUND_MISSIONS} style={userSidebarStyle.menuItem}>
//               <MoneyIcon /> Fund the mission
//             </NavLink>
//           )}

//           <NavLink to={ROUTER_URL.USER_HELP_AND_SUPPORT} style={userSidebarStyle.menuItem}>
//             <QuestionIcon /> Help & Support
//           </NavLink>
//         </Box>
//       </Box>

//       <Modal
//         onClose={() => setIsShowAddTicketModal(false)}
//         open={isShowAddTicketModal}
//         sx={userSidebarStyle.modalTickets}
//         title="Buy Tickets"
//       >
//         <AddTableTicket onCloseModal={() => setIsShowAddTicketModal(false)} onSuccess={onSuccess} />
//       </Modal>
//       <Modal
//         onClose={() => setIsNeedBeLoggedModalOpen(false)}
//         open={isNeedBeLoggedModalOpen}
//         sx={userSidebarStyle.modalLogin}
//         mainIcon={<DangerIcon />}
//       >
//         <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
//       </Modal>
//       <Modal
//         isShowSaveButton
//         mainButtonTitle={`Donate $${selectedOption}`}
//         isSaveButtonDisable={!selectedOption}
//         isShowCloseButton
//         closeButtonTitle="Close"
//         onClose={() => {
//           setIsDonationModalOpen(false);
//           setError('');
//         }}
//         open={isDonationModalOpen}
//         sx={userSidebarStyle.modalBid}
//         onSave={handleAddDonation}
//       >
//         <MakeDonationModal selectedOption={selectedOption} setSelectedOption={setSelectedOption} error={error} />
//       </Modal>
//       <Modal
//         open={isSuccessModalOpen}
//         onClose={() => {
//           setIsSuccessModalOpen(false);
//           setSelectedOption(donationOptions[0]);
//         }}
//         title="Thank You for Your Donation"
//         description={`Your donation of $${selectedOption} successfully processed.`}
//         sx={userSidebarStyle.modalLogin}
//         closeButtonTitle="Done"
//         mainIcon={<SuccessIcon />}
//       />
//       <Modal
//         open={isSuccessModalOpen2}
//         onClose={() => setIsSuccessModalOpen2(false)}
//         title="Thank You For Your Support!"
//         description="Your tickets have been successfully purchased. A receipt and additional information will be delivered by email."
//         sx={userSidebarStyle.modalLogin}
//         mainButtonTitle="Done"
//         onSave={() => setIsSuccessModalOpen2(false)}
//         isShowSaveButton
//         mainIcon={<SuccessIcon />}
//       />
//       <Modal
//         open={isAttentionModalOpen}
//         onClose={() => handleAttentionModalChange('', '')}
//         title={modalTitle}
//         description={modalDescription}
//         sx={userSidebarStyle.modalLogin}
//       />
//       {paymentStep === 1 ? (
//         <Modal
//           open={isShowPaymentModal}
//           onClose={handleClosePaymentModal}
//           title="Add Payment Method"
//           description="Adding a payment method allows you to participate in the auction and make donations."
//           isShowCloseButton
//           closeButtonTitle="Later"
//           mainButtonTitle="Add Payment Method"
//           isShowSaveButton
//           onSave={handleNextStep}
//           mainIcon={<PaymentIcon />}
//           sx={profileContactList.modal}
//         />
//       ) : (
//         <Modal
//           open={isShowPaymentModal}
//           onClose={handleClosePaymentModal}
//           title="Choose Payment Option"
//           isShowCloseButton
//           closeButtonTitle="Cancel"
//           mainButtonTitle="Confirm"
//           isShowSaveButton
//           onSave={addPaymentOption}
//           sx={profileContactList.modal}
//         >
//           <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
//         </Modal>
//       )}
//     </Box>
//   );
// };

// export default UserSidebar;
